"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Play } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { newsData } from "@/lib/news-data";
import { defaultLocale, type Locale } from "@/lib/i18n";
import { getTranslation } from "@/lib/translations";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi
} from "@/components/ui/carousel";

interface NewsSectionProps {
  locale?: Locale;
}

export function NewsSection({ locale = defaultLocale }: NewsSectionProps) {
  const t = getTranslation(locale);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);
  
  // Sort by date and get the 3 most recent news items
  const latestNews = [...newsData]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getLocalizedPath = (path: string) => {
    if (locale === defaultLocale) {
      return path;
    }
    return `/${locale}${path}`;
  };

  const renderNewsCard = (news: typeof newsData[0], index: number) => (
    <article
      className="group bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg h-full flex flex-col"
    >
      {/* Image */}
      <div className="aspect-[16/10] relative overflow-hidden">
        <img
          src={news.image}
          alt={news.title[locale] || news.title.de}
          loading="lazy"
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${news.imagePosition || 'object-center'}`}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Video Badge */}
        {news.isVideo && (
          <div className="absolute top-4 left-4">
            <Badge className="bg-red-600 text-white text-xs flex items-center gap-1">
              <Play className="h-3 w-3" fill="currentColor" />
              Video
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        {/* Date */}
        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
          <Calendar className="h-4 w-4" />
          {formatDate(news.date)}
        </div>

        {/* Title */}
        <h3 className="text-lg font-normal tracking-wide text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors">
          {news.title[locale] || news.title.de}
        </h3>

        {/* Excerpt */}
        <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
          {news.excerpt[locale] || news.excerpt.de}
        </p>

        {/* Read More Link */}
        <Link
          to={getLocalizedPath(`/${t.routes.news}/${news.id}`)}
          aria-label={`${locale === 'de' ? 'Weiterlesen' : locale === 'en' ? 'Read more' : locale === 'fr' ? 'Lire la suite' : 'Leggi di più'}: ${news.title[locale] || news.title.de}`}
          className="inline-flex items-center gap-1.5 text-primary-accessible font-medium text-sm hover:text-primary-accessible/80 transition-colors group/link"
        >
          {locale === 'de' ? 'Weiterlesen' : 
           locale === 'en' ? 'Read more' : 
           locale === 'fr' ? 'Lire la suite' : 'Leggi di più'}
          <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
        </Link>
      </div>
    </article>
  );
  return (
    <section className="py-16 lg:py-24 bg-white dark:bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-[45px] font-normal text-foreground leading-relaxed tracking-wide mb-4">
            {locale === 'de' ? 'Aktuelle News' : 
             locale === 'en' ? 'Latest News' : 
             locale === 'fr' ? 'Actualités' : 'Notizie'}
          </h2>
          <p className="text-lg md:text-[22px] font-extralight text-foreground/80 leading-relaxed tracking-wide max-w-5xl mx-auto">
            {locale === 'de' ? 'Bleiben Sie informiert über Neuigkeiten bei der W. Althaus AG.' :
             locale === 'en' ? 'Stay informed about news at W. Althaus AG.' :
             locale === 'fr' ? 'Restez informé des nouvelles chez W. Althaus AG.' :
             'Rimani informato sulle novità presso W. Althaus AG.'}
          </p>
        </motion.div>

        {/* Mobile: Carousel */}
        <div className="md:hidden px-0">
          <Carousel setApi={setApi} opts={{ loop: true }} className="w-full">
            <CarouselContent>
              {latestNews.map((news, index) => (
                <CarouselItem key={news.id} className="basis-[90%] pl-4">
                  {renderNewsCard(news, index)}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {latestNews.map((_, index) => (
              <button
                key={index}
                onClick={() => api?.scrollTo(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === current ? "w-6 bg-primary" : "w-2 bg-primary/30"
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Desktop: Grid */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {latestNews.map((news, index) => (
            <motion.div
              key={news.id}
              className="h-full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {renderNewsCard(news, index)}
            </motion.div>
          ))}
        </div>

        {/* View All Link */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Link to={getLocalizedPath(`/${t.routes.news}`)}>
            <Button 
              size="default" 
              className="w-full md:w-auto bg-primary hover:bg-primary/90 text-white px-6 py-3 text-sm group"
            >
              {locale === 'de' ? 'Alle News ansehen' : 
               locale === 'en' ? 'View all news' : 
               locale === 'fr' ? 'Voir toutes les actualités' : 'Vedi tutte le notizie'}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
